/* ============================================================
   Lumbra — storefront behaviour
   Vanilla JS. Idempotent: safe to load once and re-run per section.
   ============================================================ */
(function () {
  'use strict';

  if (window.__lumbraLoaded) return;
  window.__lumbraLoaded = true;

  document.documentElement.classList.add('lmb-js');

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Scroll reveals ----------
     We only hide content once we know IntersectionObserver exists. If it does
     not, nothing is ever hidden and the page reads normally.               */
  function initReveals(root) {
    if (!('IntersectionObserver' in window)) return;

    var targets = root.querySelectorAll('.lmb-reveal:not([data-lmb-revealed])');
    if (!targets.length) return;

    document.documentElement.classList.add('lmb-reveal-armed');

    if (reduceMotion) {
      targets.forEach(function (el) {
        el.setAttribute('data-lmb-revealed', '');
        el.classList.add('is-visible');
      });
      return;
    }

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var el = entry.target;
          var siblings = Array.prototype.slice.call(
            el.parentElement ? el.parentElement.children : []
          ).filter(function (n) {
            return n.classList && n.classList.contains('lmb-reveal');
          });
          var i = Math.max(0, siblings.indexOf(el));
          el.style.setProperty('--lmb-delay', Math.min(i, 6) * 80 + 'ms');
          el.classList.add('is-visible');
          io.unobserve(el);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
    );

    targets.forEach(function (el) {
      el.setAttribute('data-lmb-revealed', '');
      io.observe(el);
    });
  }

  /* ---------- FAQ accordion ---------- */
  function initFaq(root) {
    root.querySelectorAll('.lmb__faq-item:not([data-lmb-ready])').forEach(function (item) {
      item.setAttribute('data-lmb-ready', '');

      var btn = item.querySelector('.lmb__faq-q');
      var panel = item.querySelector('.lmb__faq-a');
      if (!btn || !panel) return;

      panel.style.height = '0px';
      btn.setAttribute('aria-expanded', 'false');
      panel.setAttribute('aria-hidden', 'true');

      btn.addEventListener('click', function () {
        var open = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', String(!open));
        panel.setAttribute('aria-hidden', String(open));

        if (open) {
          panel.style.height = panel.scrollHeight + 'px';
          requestAnimationFrame(function () {
            panel.style.height = '0px';
          });
        } else {
          panel.style.height = panel.scrollHeight + 'px';
          panel.addEventListener('transitionend', function onEnd(e) {
            if (e.propertyName !== 'height') return;
            panel.style.height = 'auto';
            panel.removeEventListener('transitionend', onEnd);
          });
        }
      });
    });
  }

  /* ---------- Anatomy hotspots ---------- */
  function initHotspots(root) {
    root.querySelectorAll('[data-lmb-anatomy]:not([data-lmb-ready])').forEach(function (wrap) {
      wrap.setAttribute('data-lmb-ready', '');

      var dots = wrap.querySelectorAll('.lmb__hotspot');
      var items = wrap.querySelectorAll('.lmb__hotspot-item');

      function activate(index) {
        items.forEach(function (item, i) {
          item.classList.toggle('is-active', i === index);
        });
        dots.forEach(function (dot, i) {
          dot.setAttribute('aria-pressed', String(i === index));
        });
      }

      dots.forEach(function (dot, i) {
        dot.addEventListener('click', function () {
          activate(i);
        });
        dot.addEventListener('mouseenter', function () {
          if (!reduceMotion) activate(i);
        });
        dot.addEventListener('focus', function () {
          activate(i);
        });
      });
    });
  }

  /* ---------- Buy box ---------- */
  function initBuyBox(root) {
    root.querySelectorAll('[data-lmb-buybox]:not([data-lmb-ready])').forEach(function (box) {
      box.setAttribute('data-lmb-ready', '');

      var form = box.querySelector('[data-lmb-form]');
      var idInput = box.querySelector('[data-lmb-variant-id]');
      var qtyInput = box.querySelector('[data-lmb-qty]');
      var priceEl = box.querySelector('[data-lmb-price]');
      var wasEl = box.querySelector('[data-lmb-was]');
      var atc = box.querySelector('[data-lmb-atc]');
      var atcLabel = box.querySelector('[data-lmb-atc-label]');
      var errorEl = box.querySelector('[data-lmb-error]');
      var mediaImg = box.querySelector('.lmb__buy-media > img');
      if (!form || !idInput || !atc) return;

      var variants = [];
      var dataEl = box.querySelector('[data-lmb-variants]');
      if (dataEl) {
        try {
          variants = JSON.parse(dataEl.textContent) || [];
        } catch (e) {
          variants = [];
        }
      }

      function showError(msg) {
        if (!errorEl) return;
        errorEl.textContent = msg;
        errorEl.classList.add('is-shown');
      }

      function clearError() {
        if (!errorEl) return;
        errorEl.classList.remove('is-shown');
      }

      /* Swap the buy-box photo when the colour changes. Preload first so the
         panel never flashes empty, and drop srcset/sizes or the browser keeps
         serving a candidate from the previous variant's set. */
      var pendingSrc = null;

      function setMedia(url) {
        if (!mediaImg || !url) return;
        pendingSrc = url;
        if (mediaImg.getAttribute('src') === url) return;
        var pre = new Image();
        pre.onload = function () {
          /* A later request won the race; don't let a slow preload overwrite it. */
          if (pendingSrc !== url) return;
          mediaImg.removeAttribute('srcset');
          mediaImg.removeAttribute('sizes');
          mediaImg.src = url;
        };
        pre.src = url;
      }

      /* Add-on gallery: thumbnails under the photo while a bundle tier is
         selected. The cushion's own photo is remembered so leaving the bundle
         (or picking the cushion thumb) puts it back. */
      var cushionSrc = mediaImg ? mediaImg.getAttribute('src') : null;

      function pressThumb(btn) {
        var strip = btn.closest('[data-lmb-gallery]');
        if (strip) {
          strip.querySelectorAll('[data-lmb-thumb]').forEach(function (t) {
            var on = t === btn;
            t.setAttribute('aria-pressed', String(on));
            t.style.borderColor = on ? 'var(--lmb-sage-deep)' : '';
          });
        }
        setMedia(btn.getAttribute('data-lmb-thumb'));
      }

      /* Show an add-on colour's photo, pressing its thumb when the strip has it. */
      function showAddonImage(url) {
        if (!url) return false;
        var match = null;
        box.querySelectorAll('[data-lmb-gallery]:not([hidden]) [data-lmb-thumb]').forEach(function (t) {
          if (!match && t.getAttribute('data-lmb-thumb') === url) match = t;
        });
        if (match) {
          pressThumb(match);
        } else {
          setMedia(url);
        }
        return true;
      }

      function syncGallery() {
        var tier = box.querySelector('[data-lmb-tier]:checked');
        var key = tier ? tier.getAttribute('data-lmb-addon-gallery') : null;
        var shown = null;
        box.querySelectorAll('[data-lmb-gallery]').forEach(function (strip) {
          var on = !!key && strip.getAttribute('data-lmb-gallery') === key;
          strip.hidden = !on;
          if (on) shown = strip;
        });
        if (!shown) {
          if (cushionSrc) setMedia(cushionSrc);
          return;
        }
        /* The default colour opens on the lead (cut-out) photo; any other
           chosen colour opens on its own photo. */
        var label = tier.closest('.lmb__tier');
        var chosen = label && label.querySelector('[data-lmb-addon-swatch][aria-pressed="true"]');
        if (chosen && !chosen.hasAttribute('data-lmb-addon-default') &&
            showAddonImage(chosen.getAttribute('data-lmb-addon-image'))) return;
        var first = shown.querySelector('[data-lmb-thumb]:not([data-lmb-thumb-main])');
        if (first) pressThumb(first);
      }

      box.addEventListener('click', function (e) {
        /* Add-on colour: buttons sit inside the tier's <label>, so clicking one
           does not select the tier by itself. Select it here. */
        var sw = e.target.closest('[data-lmb-addon-swatch]');
        if (sw && box.contains(sw)) {
          e.preventDefault();
          var label = sw.closest('.lmb__tier');
          var radio = label && label.querySelector('[data-lmb-tier]');
          if (label) {
            label.querySelectorAll('[data-lmb-addon-swatch]').forEach(function (b) {
              b.setAttribute('aria-pressed', String(b === sw));
            });
          }
          if (radio) {
            radio.setAttribute('data-lmb-addon', sw.getAttribute('data-lmb-addon-swatch'));
            if (!radio.checked) {
              radio.checked = true;
              radio.dispatchEvent(new Event('change', { bubbles: true }));
            }
          }
          showAddonImage(sw.getAttribute('data-lmb-addon-image'));
          return;
        }

        var btn = e.target.closest('[data-lmb-thumb]');
        if (btn && box.contains(btn)) pressThumb(btn);
      });

      /* Variant swatches */
      function selectVariant(id) {
        var v = variants.filter(function (x) {
          return String(x.id) === String(id);
        })[0];

        idInput.value = id;

        box.querySelectorAll('[data-lmb-swatch]').forEach(function (b) {
          b.setAttribute('aria-pressed', String(b.getAttribute('data-lmb-swatch') === String(id)));
        });

        if (v) {
          if (v.image) {
            cushionSrc = v.image;
            box.querySelectorAll('[data-lmb-thumb-main]').forEach(function (t) {
              t.setAttribute('data-lmb-thumb', v.image);
              var timg = t.querySelector('img');
              if (timg) timg.src = v.image;
              if (t.closest('[data-lmb-gallery]:not([hidden])')) pressThumb(t);
            });
          }
          setMedia(v.image);
          if (priceEl && v.price_formatted) priceEl.textContent = v.price_formatted;
          if (wasEl) {
            if (v.compare_formatted) {
              wasEl.textContent = v.compare_formatted;
              wasEl.hidden = false;
            } else {
              wasEl.hidden = true;
            }
          }
          atc.disabled = !v.available;
          if (atcLabel) {
            atcLabel.textContent = v.available
              ? atc.getAttribute('data-label-default')
              : atc.getAttribute('data-label-soldout');
          }
        }
        clearError();
      }

      box.querySelectorAll('[data-lmb-swatch]').forEach(function (b) {
        b.addEventListener('click', function () {
          selectVariant(b.getAttribute('data-lmb-swatch'));
        });
      });

      /* Quantity tiers */
      box.querySelectorAll('[data-lmb-tier]').forEach(function (input) {
        input.addEventListener('change', function () {
          if (qtyInput) qtyInput.value = input.value;
          syncGallery();
        });
      });

      /* Stepper */
      var stepper = box.querySelector('[data-lmb-stepper]');
      if (stepper && qtyInput) {
        stepper.addEventListener('click', function (e) {
          var btn = e.target.closest('[data-lmb-step]');
          if (!btn) return;
          var delta = btn.getAttribute('data-lmb-step') === 'up' ? 1 : -1;
          var next = Math.max(1, (parseInt(qtyInput.value, 10) || 1) + delta);
          qtyInput.value = next;
        });
      }

      /* Add to cart */
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        clearError();

        var qty = parseInt(qtyInput ? qtyInput.value : '1', 10) || 1;

        var items = [{ id: Number(idInput.value), quantity: qty }];

        /* Bundle tier with an add-on product: one add-on per main unit. */
        var tier = box.querySelector('[data-lmb-tier]:checked');
        var addonId = tier ? tier.getAttribute('data-lmb-addon') : null;
        if (addonId) items.push({ id: Number(addonId), quantity: qty });

        atc.classList.add('is-loading');
        atc.disabled = true;

        fetch(window.Shopify && window.Shopify.routes && window.Shopify.routes.root
          ? window.Shopify.routes.root + 'cart/add.js'
          : '/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: items })
        })
          .then(function (res) {
            return res.json().then(function (data) {
              if (!res.ok) throw new Error(data.description || data.message || 'Could not add to cart.');
              return data;
            });
          })
          .then(function () {
            /* Tell the theme the cart changed. Horizon listens for its own
               events; these are best-effort and harmless if unhandled. */
            document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
            document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true }));

            var drawer = document.querySelector('cart-drawer, [data-cart-drawer]');
            var opened = false;
            if (drawer) {
              if (typeof drawer.open === 'function') {
                try { drawer.open(); opened = true; } catch (err) { opened = false; }
              } else if (typeof drawer.showModal === 'function') {
                try { drawer.showModal(); opened = true; } catch (err) { opened = false; }
              }
            }

            if (!opened) {
              window.location.href =
                (window.Shopify && window.Shopify.routes && window.Shopify.routes.root
                  ? window.Shopify.routes.root
                  : '/') + 'cart';
            }
          })
          .catch(function (err) {
            showError(err.message || 'Something went wrong. Please try again.');
          })
          .then(function () {
            atc.classList.remove('is-loading');
            atc.disabled = false;
          });
      });
    });
  }

  /* ---------- Boot ---------- */
  function boot(root) {
    var scope = root || document;
    initReveals(scope);
    initFaq(scope);
    initHotspots(scope);
    initBuyBox(scope);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(document); });
  } else {
    boot(document);
  }

  /* Theme editor: re-init when a section is re-rendered */
  document.addEventListener('shopify:section:load', function (e) {
    boot(e.target || document);
  });

  window.LumbraBoot = boot;
})();
